import { ContentState, StatusPill } from "@whitehaven/ui";
import type {
  AdminLeadDetail,
  AdminLeadStageHistoryCursor,
  AdminLeadStageHistoryItem,
} from "@whitehaven/contracts";
import type { ReactNode } from "react";
import {
  buildAdminLeadTimeline,
  type AdminLeadTimelineItem,
} from "./admin-lead-detail-model";
import { leadSourceKindLabels } from "./admin-lead-list-model";
import { leadPipelineStageLabels } from "./pipeline-stage-model";

export type AdminLeadDetailNotice = "REFRESH_FAILED" | null;
export type AdminLeadStageHistoryNotice =
  | "REFRESH_FAILED"
  | "LOAD_MORE_FAILED"
  | null;

export type AdminLeadStageHistoryViewState =
  | { readonly kind: "unavailable" }
  | { readonly kind: "loading" }
  | { readonly kind: "error" }
  | {
      readonly kind: "ready";
      readonly items: readonly AdminLeadStageHistoryItem[];
      readonly nextCursor: AdminLeadStageHistoryCursor | null;
      readonly consumedCursors: readonly AdminLeadStageHistoryCursor[];
      readonly refreshing: boolean;
      readonly loadingMore: boolean;
      readonly notice: AdminLeadStageHistoryNotice;
    };

interface AdminLeadDetailViewProps {
  readonly detail: AdminLeadDetail;
  readonly refreshing: boolean;
  readonly notice: AdminLeadDetailNotice;
  readonly stageHistory?: AdminLeadStageHistoryViewState;
  readonly assignmentPanel?: ReactNode;
  readonly duplicatePanel?: ReactNode;
  readonly conversionPanel?: ReactNode;
  readonly appointmentConversionPanel?: ReactNode;
  readonly engagementPanel?: ReactNode;
  readonly refreshDisabled?: boolean;
  readonly onRefresh: () => void;
  readonly onRetryStageHistory?: () => void;
  readonly onLoadMoreStageHistory?: () => void;
}

const leadDetailDateFormatter = new Intl.DateTimeFormat("en-US", {
  dateStyle: "medium",
  timeStyle: "short",
  timeZone: "Pacific/Honolulu",
});

const timelineLabels = {
  LEAD_CREATED: {
    title: "Lead created",
    detail: "The lead entered the CRM.",
  },
  FIRST_TOUCH_CAPTURED: {
    title: "First-touch source captured",
    detail: "The acquisition source snapshot was recorded.",
  },
  LEAD_ARCHIVED: {
    title: "Lead archived",
    detail: "The lead record moved to its archived lifecycle state.",
  },
} as const;

export function formatAdminLeadDetailDate(value: string): string | null {
  const date = new Date(value);
  return Number.isFinite(date.getTime())
    ? leadDetailDateFormatter.format(date)
    : null;
}

function LeadDetailDate({ value }: { readonly value: string }) {
  const formatted = formatAdminLeadDetailDate(value);
  return formatted ? <time dateTime={value}>{formatted}</time> : "Unavailable";
}

function DetailFact({
  label,
  children,
}: {
  readonly label: string;
  readonly children: ReactNode;
}) {
  return (
    <div className="crm-lead-detail__fact">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function TimelineEntry({ item }: { readonly item: AdminLeadTimelineItem }) {
  const copy =
    item.kind === "PIPELINE_STAGE_CHANGED"
      ? {
          title: "Pipeline stage changed",
          detail: `${leadPipelineStageLabels[item.fromStage]} to ${
            leadPipelineStageLabels[item.toStage]
          }. This recorded change was confirmed by the CRM service.`,
        }
      : timelineLabels[item.kind];

  return (
    <li className="crm-lead-detail__timeline-item">
      <span className="crm-lead-detail__timeline-marker" aria-hidden="true" />
      <div>
        <h4>{copy.title}</h4>
        <LeadDetailDate value={item.occurredAt} />
        <p>{copy.detail}</p>
      </div>
    </li>
  );
}

export function AdminLeadDetailView({
  detail,
  refreshing,
  notice,
  stageHistory = { kind: "unavailable" },
  assignmentPanel,
  duplicatePanel,
  conversionPanel,
  appointmentConversionPanel,
  engagementPanel,
  refreshDisabled = false,
  onRefresh,
  onRetryStageHistory,
  onLoadMoreStageHistory,
}: AdminLeadDetailViewProps) {
  const { summary } = detail;
  const stageHistoryItems =
    stageHistory.kind === "ready" ? stageHistory.items : [];
  const timeline = buildAdminLeadTimeline(detail, stageHistoryItems);
  const sourceLabel = summary.sourceKind
    ? leadSourceKindLabels[summary.sourceKind]
    : "Not captured";

  return (
    <div
      className="crm-lead-detail"
      aria-busy={refreshing ? "true" : undefined}
    >
      {notice && (
        <ContentState
          className="crm-lead-detail__notice"
          kind="error"
          headingLevel={2}
          title="The lead details could not be refreshed."
          actions={
            <button
              type="button"
              className="crm-lead-detail__button"
              disabled={refreshing || refreshDisabled}
              onClick={onRefresh}
            >
              Try again
            </button>
          }
          live
        >
          <p>The last confirmed details remain visible.</p>
        </ContentState>
      )}

      <div className="crm-lead-detail__heading">
        <div>
          <p className="portal-kicker">Authorized lead</p>
          <h2>{summary.displayName}</h2>
          <p>
            Current contact, workflow, source, and record facts from the secure
            CRM boundary.
          </p>
        </div>
        <button
          type="button"
          className="crm-lead-detail__button"
          onClick={onRefresh}
          disabled={refreshing || refreshDisabled}
        >
          {refreshing ? "Refreshing" : "Refresh details"}
        </button>
      </div>

      <div className="crm-lead-detail__grid">
        <section
          className="crm-lead-detail__card"
          aria-labelledby="crm-lead-contact-title"
        >
          <h3 id="crm-lead-contact-title">Contact</h3>
          <dl>
            <DetailFact label="Name">{summary.displayName}</DetailFact>
            <DetailFact label="Email">
              {summary.emailNormalized ?? "Not recorded"}
            </DetailFact>
            <DetailFact label="Phone">
              {summary.phoneE164 ?? "Not recorded"}
            </DetailFact>
            <DetailFact label="Contact record">
              <StatusPill
                tone={
                  summary.contactRecordStatus === "ARCHIVED"
                    ? "warning"
                    : "neutral"
                }
              >
                {summary.contactRecordStatus === "ARCHIVED"
                  ? "Archived"
                  : "Active"}
              </StatusPill>
            </DetailFact>
          </dl>
        </section>

        {assignmentPanel}

        <section
          className="crm-lead-detail__card"
          aria-labelledby="crm-lead-workflow-title"
        >
          <h3 id="crm-lead-workflow-title">Workflow</h3>
          <dl>
            <DetailFact label="Pipeline stage">
              <StatusPill>
                {leadPipelineStageLabels[summary.pipelineStage]}
              </StatusPill>
            </DetailFact>
            <DetailFact label="Lead record">
              <StatusPill
                tone={
                  summary.leadRecordStatus === "ARCHIVED"
                    ? "warning"
                    : "neutral"
                }
              >
                {summary.leadRecordStatus === "ARCHIVED"
                  ? "Archived"
                  : "Active"}
              </StatusPill>
            </DetailFact>
            <DetailFact label="Created">
              <LeadDetailDate value={summary.createdAt} />
            </DetailFact>
            <DetailFact label="Last updated">
              <LeadDetailDate value={summary.updatedAt} />
            </DetailFact>
            {detail.leadArchivedAt && (
              <DetailFact label="Lead archived">
                <LeadDetailDate value={detail.leadArchivedAt} />
              </DetailFact>
            )}
          </dl>
        </section>

        <section
          className="crm-lead-detail__card"
          aria-labelledby="crm-lead-source-title"
        >
          <h3 id="crm-lead-source-title">Acquisition</h3>
          <dl>
            <DetailFact label="Source">{sourceLabel}</DetailFact>
            <DetailFact label="Source key">
              {summary.sourceKey ?? "Not recorded"}
            </DetailFact>
            <DetailFact label="Campaign key">
              {summary.campaignKey ?? "Not recorded"}
            </DetailFact>
            <DetailFact label="Captured">
              {detail.sourceCapturedAt ? (
                <LeadDetailDate value={detail.sourceCapturedAt} />
              ) : (
                "Not recorded"
              )}
            </DetailFact>
          </dl>
        </section>
      </div>

      {duplicatePanel}

      {conversionPanel}

      {appointmentConversionPanel}

      {engagementPanel}

      <section
        className="crm-lead-detail__timeline"
        aria-labelledby="crm-lead-timeline-title"
        aria-describedby="crm-lead-timeline-scope"
      >
        <div className="crm-lead-detail__section-heading">
          <div>
            <h3 id="crm-lead-timeline-title">Timeline</h3>
            <p id="crm-lead-timeline-scope">
              Confirmed creation, first-touch capture, archival, and recorded
              pipeline changes. Pipeline history begins with CRM-006 and does
              not claim activity from before that ledger existed.
            </p>
          </div>
        </div>

        {timeline.length > 0 ? (
          <ol className="crm-lead-detail__timeline-list">
            {timeline.map((item) => (
              <TimelineEntry
                key={`${item.kind}:${item.occurredAt}:${
                  item.kind === "PIPELINE_STAGE_CHANGED"
                    ? item.pipelineRevision
                    : "foundation"
                }`}
                item={item}
              />
            ))}
          </ol>
        ) : (
          <ContentState
            kind="empty"
            headingLevel={4}
            title="No dated lead history is available."
          >
            <p>
              The authorized detail exists, but no canonical timeline facts
              could be confirmed.
            </p>
          </ContentState>
        )}

        <div
          className="crm-lead-detail__history-state"
          aria-busy={
            stageHistory.kind === "loading" ||
            (stageHistory.kind === "ready" &&
              (stageHistory.refreshing || stageHistory.loadingMore))
              ? "true"
              : undefined
          }
        >
          {stageHistory.kind === "unavailable" && (
            <ContentState
              kind="warning"
              headingLevel={4}
              title="Recorded pipeline history is unavailable."
            >
              <p>
                Foundation facts remain visible, but the protected history
                service has not been connected.
              </p>
            </ContentState>
          )}

          {stageHistory.kind === "loading" && (
            <ContentState
              kind="loading"
              headingLevel={4}
              title="Loading recorded pipeline history."
            >
              <p>Confirmed foundation facts remain visible above.</p>
            </ContentState>
          )}

          {stageHistory.kind === "error" && (
            <ContentState
              kind="error"
              headingLevel={4}
              title="Recorded pipeline history could not be loaded."
              actions={
                onRetryStageHistory ? (
                  <button
                    type="button"
                    className="crm-lead-detail__button"
                    onClick={onRetryStageHistory}
                  >
                    Try history again
                  </button>
                ) : undefined
              }
            >
              <p>No unconfirmed stage changes are shown.</p>
            </ContentState>
          )}

          {stageHistory.kind === "ready" && stageHistory.items.length === 0 && (
            <ContentState
              kind="empty"
              headingLevel={4}
              title="No recorded pipeline changes are available."
            >
              <p>
                This confirms only that the CRM-006 history read returned no
                retained transitions. It does not describe earlier activity.
              </p>
            </ContentState>
          )}

          {stageHistory.kind === "ready" && stageHistory.notice && (
            <ContentState
              kind="error"
              headingLevel={4}
              title={
                stageHistory.notice === "REFRESH_FAILED"
                  ? "Pipeline history could not be refreshed."
                  : "More pipeline history could not be loaded."
              }
              actions={
                <button
                  type="button"
                  className="crm-lead-detail__button"
                  onClick={
                    stageHistory.notice === "REFRESH_FAILED"
                      ? onRetryStageHistory
                      : onLoadMoreStageHistory
                  }
                >
                  Try again
                </button>
              }
            >
              <p>The last confirmed history remains visible.</p>
            </ContentState>
          )}

          {stageHistory.kind === "ready" && stageHistory.nextCursor && (
            <button
              type="button"
              className="crm-lead-detail__button"
              aria-disabled={
                stageHistory.loadingMore || stageHistory.refreshing
                  ? "true"
                  : undefined
              }
              onClick={() => {
                if (
                  !stageHistory.loadingMore &&
                  !stageHistory.refreshing &&
                  onLoadMoreStageHistory
                ) {
                  onLoadMoreStageHistory();
                }
              }}
            >
              {stageHistory.loadingMore
                ? "Loading history"
                : "Load more pipeline history"}
            </button>
          )}

          {stageHistory.kind === "ready" &&
            (stageHistory.refreshing || stageHistory.loadingMore) && (
              <p className="crm-lead-detail__refresh-status" role="status">
                {stageHistory.refreshing
                  ? "Refreshing recorded pipeline history. The last confirmed history remains available."
                  : "Loading more recorded pipeline history. The last confirmed history remains available."}
              </p>
            )}
        </div>
      </section>

      {refreshing && (
        <p className="crm-lead-detail__refresh-status" role="status">
          Refreshing the lead details. The last confirmed view remains
          available.
        </p>
      )}
    </div>
  );
}
